import { Injectable } from '@angular/core';
import { chain as _chain, filter, map as _map } from 'lodash';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MunicipalityService } from '../api/municipality.service';
import { UserService } from '../api/user.service';

@Injectable({
  providedIn: 'root',
})
export class MunicipalityStoreService {
  private readonly municipalityStore: BehaviorSubject<any> = new BehaviorSubject<any[]>([]);

  private readonly pagedMunicipalityStore: BehaviorSubject<any> = new BehaviorSubject<any[]>([]);

  private _unsubscribe$: Subject<boolean> = new Subject();

  public readonly allMunicipalities$: Observable<any[]> = this.municipalityStore.asObservable();

  public readonly pagedMunicipalities$ = this.pagedMunicipalityStore.asObservable();

  private pageSize = 5;
  private pageIndex = 0;

  constructor(
    private municipalityService: MunicipalityService,
    private userService: UserService,
  ) {}

  getAllMunicipalities = () => {
    const modifyUserGuids: string[] = [];

    this.municipalityService
      .getAllMunicipalities()
      .pipe(
        map((municipalities: any) => {
          _map(municipalities.data, (municipalityDetails: any) => {
            const modifyUserGuidLength = 36;
            if (municipalityDetails.modifyUser.length === modifyUserGuidLength && !modifyUserGuids.includes(municipalityDetails.modifyUser)) {
              modifyUserGuids.push(municipalityDetails.modifyUser);
            }
          });
          return municipalities.data;
        }),
        switchMap((municipalities) => this.getModifiedUserName(municipalities, modifyUserGuids)),
        tap((municipalities) => this.municipalityStore.next(municipalities)),
        tap((municipalities: any) => {
          this.pagedMunicipalityStore.next(this.paginate(municipalities, this.pageIndex, this.pageSize));
        }),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();
  };

  public filterMunicipalities = (pageIndex: number, pageSize: number) => {
    return this.allMunicipalities$.pipe(
      map((municipalities) => this.paginate(municipalities, pageIndex, pageSize)),
      tap((filteredMunicipality) => {
        this.pagedMunicipalityStore.next(filteredMunicipality);
      }),
    );
  };

  private getModifiedUserName = (municipalities: any, modifyUserGuids: string[]) => {
    return modifyUserGuids.length > 0
      ? this.userService.getFullNameByGuid(modifyUserGuids).pipe(
          map((userDetails: any) => {
            _map(municipalities, (municipality) => {
              return filter(userDetails, (user) => {
                municipality.modifyUser = this.getUserName(municipality, user);
                return municipality;
              });
            });
            return municipalities;
          }),
        )
      : of(municipalities);
  };

  private paginate = (municipalities: any[], pageIndex: number, pageSize: number) => {
    return _chain(municipalities)
      .orderBy(['name'], ['asc'])
      .drop(pageIndex * pageSize)
      .take(pageSize)
      .value();
  };

  private getUserName = (municipality: any, user: any): string => {
    return municipality.modifyUser.toLowerCase() === user.guid.toLowerCase() ? user.firstname + ' ' + user.lastname : municipality.modifyUser;
  };

  public initializeMunicipalities() {
    this.getAllMunicipalities();
  }
}
